/**
 * The map. Leaflet, five overlays, one long-press gesture.
 *
 * Layer choices carry vessel-map's decisions across, and one of them is a
 * safety rule rather than a preference:
 *
 * - **Kartverket's sea chart is the default.** It is the only layer here that
 *   is an actual nautical chart, and it is the only one called "Chart".
 *   OpenStreetMap is offered as an orientation layer and is labelled
 *   "Overview", never "Chart" -- vessel-map's decision 9, and it holds harder
 *   here, because this screen is read on a bridge.
 *
 *   The two were labelled "Sjøkart" and "Oversikt", which was the one piece of
 *   Norwegian left in a captain-facing screen (decision 17: captain copy is
 *   English). The distinction the labels carry is the point, not the language
 *   -- so they are Chart and Overview, and "Overview" must never become
 *   "Chart".
 * - **All layers stay in EPSG:3857.** Kartverket also publishes EPSG:25833,
 *   which is more correct for Norway, but mixing projections needs Proj4Leaflet
 *   and breaks the global layer (vessel-map decision 5).
 * - **Markers cluster.** 450+ registry pins is the design load; without
 *   clustering the fjords are a solid block of icons at national zoom.
 * - **Kystverket's speed limits and Kartverket's wrecks are WMS overlays, off
 *   by default.** All three advertise EPSG:3857, which is the only reason they
 *   can be dropped in at all -- see the projection rule above. WMS rather than
 *   WFS: a WFS hands back features to parse, style and load-manage, and these
 *   are orientation rather than data this app computes with.
 * - **The speed limits are two layers, not one.** Kystverket publish them per
 *   vessel class and the sets differ. A yacht may be a næringsfartøy while her
 *   tender is a fritidsfartøy, so merging them would hide restrictions from
 *   whichever crew read the wrong one.
 *
 * Neighbour markers are drawn from whatever `/api/neighbours` returned. This
 * component does no filtering of its own and must not start: the server decides
 * what a captain may see, and a client-side rule would be a second, weaker
 * definition of the thing this product exists to get right.
 */

import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

import L from 'leaflet'
import 'leaflet.markercluster'
import { useEffect, useRef } from 'react'
import { formatLoa, formatPosition } from '@abg/ui'
import { formatWindowForCaptain } from '@abg/ui/time'
import { formatDayMonth, parseFeatureInfo, toSpeedZone } from '@abg/ui/wms'
import type { Anchorage, Application, Neighbour } from '../lib/types'

// Bergen approaches: where most of ABG's work is, and a sane cold start.
const DEFAULT_CENTER: [number, number] = [60.39, 5.32]
const DEFAULT_ZOOM = 9

export interface MapViewProps {
  anchorages: Anchorage[]
  applications: Application[]
  neighbours: Neighbour[]
  /** A pin the captain is currently placing. */
  pin?: { lat: number; lon: number } | null
  onPick?: (lat: number, lon: number) => void
  onAnchorageClick?: (anchorage: Anchorage) => void
  center?: [number, number]
  zoom?: number
  className?: string
}

export function MapView({
  anchorages,
  applications,
  neighbours,
  pin,
  onPick,
  onAnchorageClick,
  center,
  zoom,
  className,
}: MapViewProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layersRef = useRef<{
    anchorages: L.MarkerClusterGroup
    mine: L.LayerGroup
    friends: L.LayerGroup
    neighbours: L.LayerGroup
    pin: L.LayerGroup
  } | null>(null)
  // Callbacks live in a ref so changing a handler does not tear down the map.
  const handlers = useRef({ onPick, onAnchorageClick })
  handlers.current = { onPick, onAnchorageClick }

  useEffect(() => {
    if (!hostRef.current || mapRef.current) return

    const map = L.map(hostRef.current, {
      center: center ?? DEFAULT_CENTER,
      zoom: zoom ?? DEFAULT_ZOOM,
      zoomControl: true,
      // Leaflet's default tap handler swallows the long-press we need.
      tapHold: false,
    })

    const sjokart = L.tileLayer(
      'https://cache.kartverket.no/v1/service?layer=sjokartraster&style=default' +
        '&tilematrixset=webmercator&Service=WMTS&Request=GetTile&Version=1.0.0' +
        '&Format=image/png&TileMatrix={z}&TileCol={x}&TileRow={y}',
      {
        maxZoom: 18,
        attribution: '&copy; <a href="https://kartverket.no/">Kartverket</a>',
      },
    )
    const oversikt = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    })
    const openseamap = L.tileLayer('https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png', {
      maxZoom: 18,
      opacity: 0.9,
      attribution: '&copy; OpenSeaMap',
    })

    // Statutory speed limits, from Kystverket. WMS rather than their WFS: WFS
    // hands back features to parse, style and load-manage ourselves, and a
    // rendered overlay is what a captain wants -- this is orientation, not data
    // we compute with.
    //
    // **Two layers, because there are two vessel classes and the limits
    // genuinely differ.** Kystverket publish the same restrictions once for
    // næringsfartøy (`layer_762`) and once for fritidsfartøy (`layer_754`), and
    // over Bergen harbour the pleasure-craft zones cover materially more water.
    // That matters here specifically: a 72 m yacht is a næringsfartøy, and the
    // tender taking her guests ashore is not -- so a captain reading only the
    // commercial layer would send a tender into restrictions they never saw.
    // ABG's own point, and the reason this is not one merged layer.
    //
    // Each label names its class. Collapsing them into "Speed limits" and
    // picking one silently was the unsafe option: a privately registered yacht
    // is a fritidsfartøy too, and would have been reading the wrong set.
    const wmsBase = 'https://services.kystverket.no/wms.ashx'
    const kystverket = {
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      maxZoom: 18,
      opacity: 0.85,
      attribution: '&copy; <a href="https://kystverket.no/">Kystverket</a>',
    } as const

    const speedLimits = L.tileLayer.wms(wmsBase, { ...kystverket, layers: 'layer_762' })
    const speedLimitsTender = L.tileLayer.wms(wmsBase, { ...kystverket, layers: 'layer_754' })

    // Wrecks and obstructions, from Kartverket's own chart data (CC BY 4.0).
    // `vrak_og_hefter_wms` is the group: wrecks and foul ground together, which
    // is how they matter when you are choosing where the anchor goes.
    const wrecks = L.tileLayer.wms('https://wms.geonorge.no/skwms1/wms.vrak_hefter', {
      layers: 'vrak_og_hefter_wms',
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      maxZoom: 18,
      attribution: '&copy; <a href="https://kartverket.no/">Kartverket</a>',
    })

    sjokart.addTo(map)

    const anchorageLayer = L.markerClusterGroup({
      maxClusterRadius: 44,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
    })
    const mineLayer = L.layerGroup()
    // Friends and opt-in neighbours are separate toggles, not one 'others'
    // bucket: they mean different things to a captain. A friend is someone
    // you are travelling with and whose live position you can see; an opt-in
    // neighbour is a stranger who happens to have shared this one booking.
    const friendLayer = L.layerGroup()
    const neighbourLayer = L.layerGroup()
    const pinLayer = L.layerGroup()
    ;[anchorageLayer, mineLayer, friendLayer, neighbourLayer, pinLayer].forEach((layer) =>
      layer.addTo(map),
    )

    L.control
      .layers(
        // "Chart" is a chart. "Overview" is not, and is never labelled as one.
        { Chart: sjokart, Overview: oversikt },
        {
          'Sea marks': openseamap,
          // Both off until asked for. They are advisory overlays on top of the
          // chart, and three of them on by default would bury the chart the
          // captain is actually reading.
          'Wrecks & obstructions': wrecks,
          'Speed limits (commercial)': speedLimits,
          'Speed limits (pleasure & tender)': speedLimitsTender,
          Anchorages: anchorageLayer,
          'My applications': mineLayer,
          Friends: friendLayer,
          'Others sharing': neighbourLayer,
        },
        { position: 'topright', collapsed: true },
      )
      .addTo(map)

    // Click a speed zone to read the limit.
    //
    // The overlay is colour-coded and carries no numbers at the zoom a captain
    // reads, so it answers "there is a restriction here" and not "what is it".
    // Both layers are `queryable`, and Kystverket send
    // `Access-Control-Allow-Origin: *`, so this is a plain fetch -- no proxy,
    // unlike the CSC chart cutout where Kartverket's missing CORS forced
    // server-side rendering.
    //
    // A plain click was free: `onPick` fires on `contextmenu` and on the
    // long-press timer, and anchorage markers have their own handler. With both
    // speed layers off this does nothing at all, which is why it can be
    // unconditional.
    const speedSources: { label: string; layer: L.TileLayer.WMS; id: string }[] = [
      { label: 'Commercial', layer: speedLimits, id: 'layer_762' },
      { label: 'Pleasure & tender', layer: speedLimitsTender, id: 'layer_754' },
    ]

    map.on('click', (event: L.LeafletMouseEvent) => {
      const active = speedSources.filter((s) => map.hasLayer(s.layer))
      if (active.length === 0) return

      const size = map.getSize()
      const point = map.latLngToContainerPoint(event.latlng)
      const bounds = map.getBounds()
      // WMS 1.3.0 with EPSG:3857 takes the bbox in projected metres, x first.
      const sw = L.CRS.EPSG3857.project(bounds.getSouthWest())
      const ne = L.CRS.EPSG3857.project(bounds.getNorthEast())

      const popup = L.popup({ maxWidth: 300 })
        .setLatLng(event.latlng)
        .setContent('Reading the speed limit…')
        .openOn(map)

      void Promise.all(
        active.map(async (source) => {
          const query = new URLSearchParams({
            service: 'WMS',
            request: 'GetFeatureInfo',
            version: '1.3.0',
            layers: source.id,
            query_layers: source.id,
            styles: '',
            crs: 'EPSG:3857',
            bbox: `${sw.x},${sw.y},${ne.x},${ne.y}`,
            width: String(size.x),
            height: String(size.y),
            i: String(Math.round(point.x)),
            j: String(Math.round(point.y)),
            info_format: 'text/plain',
            feature_count: '5',
          })
          try {
            const response = await fetch(`${wmsBase}?${query}`)
            if (!response.ok) return { source, zones: null }
            return {
              source,
              zones: parseFeatureInfo(await response.text()).map(toSpeedZone),
            }
          } catch {
            // Offline, or the service is down. Reported as unknown rather than
            // as "no limit" -- those mean opposite things to somebody steering.
            return { source, zones: null }
          }
        }),
      ).then((results) => {
        if (!map.hasLayer(popup)) return
        popup.setContent(speedPopup(results))
      })
    })

    L.control.scale({ imperial: false, metric: true }).addTo(map)

    // Long-press to drop a pin. Touch has no contextmenu, so this is a manual
    // timer; 550 ms is long enough not to fire while panning and short enough
    // not to feel broken. Any movement over ~12 px cancels it.
    let pressTimer: number | undefined
    let pressStart: L.Point | null = null

    const cancelPress = () => {
      if (pressTimer) window.clearTimeout(pressTimer)
      pressTimer = undefined
      pressStart = null
    }

    map.on('contextmenu', (event: L.LeafletMouseEvent) => {
      handlers.current.onPick?.(event.latlng.lat, event.latlng.lng)
    })
    // Cast required, and only for the multi-event registrations. Leaflet's
    // typings give a specific payload for a single event name ('contextmenu'
    // above needs no cast) but fall back to the generic LeafletEventHandlerFn
    // for a space-separated list, which loses `latlng` and `containerPoint`.
    // The events named here all carry a mouse payload.
    map.on('mousedown touchstart', ((event: L.LeafletMouseEvent) => {
      if (!handlers.current.onPick) return
      pressStart = event.containerPoint
      pressTimer = window.setTimeout(() => {
        handlers.current.onPick?.(event.latlng.lat, event.latlng.lng)
        cancelPress()
      }, 550)
    }) as L.LeafletEventHandlerFn)
    map.on('mousemove touchmove', ((event: L.LeafletMouseEvent) => {
      if (pressStart && event.containerPoint.distanceTo(pressStart) > 12) cancelPress()
    }) as L.LeafletEventHandlerFn)
    map.on('mouseup touchend dragstart zoomstart', cancelPress)

    mapRef.current = map
    layersRef.current = {
      anchorages: anchorageLayer,
      mine: mineLayer,
      friends: friendLayer,
      neighbours: neighbourLayer,
      pin: pinLayer,
    }

    return () => {
      cancelPress()
      map.remove()
      mapRef.current = null
      layersRef.current = null
    }
    // Mount once. `center`/`zoom` are initial values; later changes go through
    // the effect below so the map is not rebuilt on every pan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (mapRef.current && center) mapRef.current.setView(center, zoom ?? mapRef.current.getZoom())
  }, [center, zoom])

  // ------------------------------------------------------------- anchorages
  useEffect(() => {
    const layer = layersRef.current?.anchorages
    if (!layer) return
    layer.clearLayers()
    for (const anchorage of anchorages) {
      const marker = L.circleMarker([anchorage.lat, anchorage.lon], anchorageStyle(anchorage))
      marker.bindTooltip(anchorage.name ?? 'Unnamed anchorage', { direction: 'top' })
      marker.bindPopup(anchoragePopup(anchorage))
      marker.on('click', () => handlers.current.onAnchorageClick?.(anchorage))
      layer.addLayer(marker)
    }
  }, [anchorages])

  // -------------------------------------------------------- my applications
  useEffect(() => {
    const layer = layersRef.current?.mine
    if (!layer) return
    layer.clearLayers()
    for (const application of applications) {
      const at: [number, number] =
        application.anchor_down_lat !== null && application.anchor_down_lon !== null
          ? [application.anchor_down_lat, application.anchor_down_lon]
          : [application.lat, application.lon]
      L.circleMarker(at, {
        radius: 9,
        weight: 3,
        color: `var(--state-${application.state})`,
        fillColor: `var(--state-${application.state})`,
        fillOpacity: 0.35,
      })
        .bindPopup(
          `<strong>${escapeHtml(application.reference)}</strong><br>` +
            `${escapeHtml(formatWindowForCaptain(application.window_start, application.window_end))}<br>` +
            `<span class="mono">${escapeHtml(formatPosition(at[0], at[1]))}</span>`,
        )
        .addTo(layer)
    }
  }, [applications])

  // ------------------------------------------------- friends and neighbours
  useEffect(() => {
    const friendLayer = layersRef.current?.friends
    const neighbourLayer = layersRef.current?.neighbours
    if (!friendLayer || !neighbourLayer) return
    friendLayer.clearLayers()
    neighbourLayer.clearLayers()
    for (const neighbour of neighbours) {
      const layer = neighbour.via_friend_group ? friendLayer : neighbourLayer
      // Friends get their live anchor-down fix when there is one; everyone else
      // gets the requested position. That split is the server's, not ours --
      // anchor_down_lat simply arrives null when we are not entitled to it.
      const at: [number, number] =
        neighbour.anchor_down_lat !== null && neighbour.anchor_down_lon !== null
          ? [neighbour.anchor_down_lat, neighbour.anchor_down_lon]
          : [neighbour.lat, neighbour.lon]
      L.circleMarker(at, {
        radius: 8,
        weight: 2,
        color: neighbour.via_friend_group ? 'var(--abg-signal)' : 'var(--abg-grey-mid)',
        dashArray: neighbour.via_friend_group ? undefined : '3 3',
        fillOpacity: 0.15,
      })
        .bindPopup(neighbourPopup(neighbour, at))
        .addTo(layer)
    }
  }, [neighbours])

  // -------------------------------------------------------------------- pin
  useEffect(() => {
    const layer = layersRef.current?.pin
    if (!layer) return
    layer.clearLayers()
    if (!pin) return
    L.circleMarker([pin.lat, pin.lon], {
      radius: 11,
      weight: 3,
      color: 'var(--abg-navy)',
      fillColor: 'var(--abg-band)',
      fillOpacity: 0.8,
    }).addTo(layer)
  }, [pin])

  return <div ref={hostRef} className={className ?? 'map-shell'} role="application" aria-label="Chart" />
}

/**
 * The speed-limit popup.
 *
 * Three states per layer and they must not blur together: a limit, no zone
 * here, and *we could not ask*. The last one is why the fetch failure is not
 * folded into the second -- "no restriction" and "we do not know" mean opposite
 * things to somebody with a hand on the throttle.
 *
 * The vessel class is named on every line. That is the whole reason there are
 * two layers (decision 52), and a popup that just said "5 knots" would undo it.
 */
function speedPopup(
  results: { source: { label: string }; zones: ReturnType<typeof toSpeedZone>[] | null }[],
): string {
  const blocks = results.map(({ source, zones }) => {
    const heading = `<div class="speed-class">${escapeHtml(source.label)}</div>`

    if (zones === null) {
      return (
        heading +
        '<div class="speed-unknown">Could not reach Kystverket. This is not the ' +
        'same as no restriction — check the chart.</div>'
      )
    }
    if (zones.length === 0) {
      return heading + '<div class="speed-none">No speed limit recorded here.</div>'
    }

    return (
      heading +
      zones
        .map((zone) => {
          const lines: string[] = []
          lines.push(
            zone.knots === null
              ? '<div class="speed-value speed-unknown">Limit not stated</div>'
              : `<div class="speed-value">${zone.knots} knots</div>`,
          )
          if (zone.area) lines.push(`<div>${escapeHtml(zone.area)}</div>`)
          // Only when it is not the whole year: printing "1 Jan - 31 Dec" on
          // every zone would bury the seasonal ones that actually matter.
          if (!zone.allYear && (zone.validFrom || zone.validTo)) {
            const from = formatDayMonth(zone.validFrom) ?? '?'
            const to = formatDayMonth(zone.validTo) ?? '?'
            lines.push(`<div><strong>${escapeHtml(from)} – ${escapeHtml(to)} only</strong></div>`)
          }
          if (zone.bufferZone) {
            lines.push(`<div>Buffer zone: ${escapeHtml(zone.bufferZone)} m</div>`)
          }
          if (zone.otherRules) lines.push(`<div><em>${escapeHtml(zone.otherRules)}</em></div>`)
          if (zone.regulationUrl) {
            lines.push(
              `<div><a href="${escapeHtml(zone.regulationUrl)}" target="_blank" ` +
                `rel="noopener noreferrer">${escapeHtml(zone.regulationName ?? 'The regulation')}` +
                '</a></div>',
            )
          } else if (zone.regulationName) {
            lines.push(`<div>${escapeHtml(zone.regulationName)}</div>`)
          }
          return `<div class="speed-zone">${lines.join('')}</div>`
        })
        .join('')
    )
  })

  return (
    `<div class="speed-popup">${blocks.join('')}` +
    '<div class="speed-source">Kystverket. Advisory — the regulation is the authority.</div>' +
    '</div>'
  )
}

function anchorageStyle(anchorage: Anchorage): L.CircleMarkerOptions {
  const base: L.CircleMarkerOptions = { radius: 6, weight: 2, fillOpacity: 0.7 }
  switch (anchorage.status) {
    case 'blocked':
      return { ...base, color: 'var(--flag-red)', fillColor: 'var(--flag-red-wash)' }
    case 'discouraged':
      return { ...base, color: 'var(--flag-amber)', fillColor: 'var(--flag-amber-wash)' }
    default:
      return { ...base, color: 'var(--sea-green)', fillColor: 'var(--sea-green-wash)' }
  }
}

function anchoragePopup(anchorage: Anchorage): string {
  const lines = [`<strong>${escapeHtml(anchorage.name ?? 'Unnamed anchorage')}</strong>`]
  if (anchorage.municipality) lines.push(escapeHtml(anchorage.municipality))
  lines.push(`<span class="mono">${escapeHtml(formatPosition(anchorage.lat, anchorage.lon))}</span>`)
  if (anchorage.depth_notes) lines.push(escapeHtml(anchorage.depth_notes))
  if (anchorage.holding_notes) lines.push(escapeHtml(anchorage.holding_notes))
  if (anchorage.status !== 'active') {
    const word = anchorage.status === 'blocked' ? 'Blocked' : 'Discouraged'
    lines.push(
      `<strong>${word}.</strong> ${escapeHtml(anchorage.status_reason ?? 'Ask us before applying.')}`,
    )
  }
  if (anchorage.restrictions) lines.push(`<em>${escapeHtml(anchorage.restrictions)}</em>`)
  return lines.join('<br>')
}

function neighbourPopup(neighbour: Neighbour, at: [number, number]): string {
  return [
    `<strong>${escapeHtml(neighbour.vessel_name)}</strong>`,
    `LOA ${escapeHtml(formatLoa(neighbour.loa_m))}`,
    escapeHtml(formatWindowForCaptain(neighbour.window_start, neighbour.window_end)),
    `<span class="mono">${escapeHtml(formatPosition(at[0], at[1]))}</span>`,
    neighbour.via_friend_group ? '<em>Friend group</em>' : '<em>Shared for this window</em>',
  ].join('<br>')
}

/** Leaflet popups take an HTML string, so anything interpolated is escaped. */
function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (ch) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] ?? ch,
  )
}
